import type { DirectorGuidanceResponse, DirectorGuidanceSession, DirectorNextStep, OnboardingResponse, OnboardingStepResponse, RecommendationScope } from '@league/shared/contract';
import type { Step } from '@league/shared/onboarding';
import type { DirectorLifecycleResult } from '@league/shared/league-lifecycle';
import { ActionQueueItem, Button, Card, LINK_CLASS, buttonClass } from '@league/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { apiFetch, apiPost } from '../../lib/api';
import { DirectorLifecycleBadge } from '../leagues/DirectorLifecycleBadge';
import { useLeagueCreationAccess } from '../leagues/useLeagueCreationAccess';
import { useRunsOnlyNights } from '../../lib/capabilities';
import { useDirectorLifecycle } from '../leagues/useDirectorLifecycle';
import type { Actor } from '../../lib/auth';

/**
 * THE DIRECTOR'S FRONT PAGE — UI-UX-SPEC §6.1.
 *
 * It was a placeholder, on the one screen every director lands on. She signed in and the product's
 * first sentence to her was "not built yet".
 *
 * THE ORDER IS THE ARGUMENT. Today, then what needs her, then her leagues. A director opens this
 * in the hour before a session or the morning after one, and both of those have a single obvious
 * question. Everything else is reference.
 *
 * NEXT STEPS are a read-only projection of roster, session and score evidence. Optional skips
 * live in the Director’s existing onboarding JSON; operational state is never changed here.
 * The existing task screen remains available for the wider operational queue.
 *
 * And it uses the kit: `Card`, `MetricTile`, `ActionQueueItem` and `StatusChip`, which an audit
 * found had zero usages across 85 screen files. They were built, documented and never adopted.
 */

interface Session {
  sessionId: string;
  leagueId: string;
  leagueName: string;
  sessionNumber: number;
  sessionTotal: number;
  startsAt: string;
  venueName: string | null;
  timeZone: string;
  daysAway: number;
  playing: number;
  tables: number;
  seatingPublished: boolean;
  seatingValid: boolean;
  scoringState: DirectorGuidanceSession['scoringState'];
  absences: number;
  subs: number;
  scored: boolean;
}

interface LeagueCard {
  id: string;
  name: string;
  status: string;
  weekday: number;
  playerCap: number;
  enrolled: number;
  sessionCount: number;
  scoredSessions: number;
  firstSessionAt: string | null;
  lifecycleSessions: { startsAt: string; status: string; hasSavedSeating: boolean }[];
}

interface Task {
  id: string;
  title: string;
  detail: string | null;
  dueDate: string | null;
  leagueName: string | null;
  overdue: boolean;
  autoGenerated: boolean;
  action?: {href: string; label: string} | null;
}

/**
 * Every date on this screen is read in the LEAGUE's zone, which the API sends with the session.
 *
 * It was a hardcoded Eastern. "Today · 7:00 PM" for a Nashville director said 8:00, an hour after
 * her players had already sat down — and after 11pm her own dashboard moved on from the session she
 * was standing in.
 */
/**
 * A TASK IS NOT A LEAGUE. Some are attached to one and some are HQ's own, so there is no league zone
 * to read a due date in. It is a date somebody typed, not an instant, and it is shown as typed.
 *
 * HQ IS IN DENVER. This said `America/New_York` for a day, which was the same mistake the rest of
 * the platform had just been cured of, moved twelve hundred miles: a default picked because the
 * demo data happened to be eastern rather than because anybody is there.
 */

const when = (iso: string, zone: string, opts: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: zone, ...opts }).format(new Date(iso));

export function DirectorDashboard({ actor }: { actor: Actor }) {
  const dashboard = useQuery({
    queryKey: ['director-dashboard'],
    queryFn: () => apiFetch<{ sessions: Session[]; leagues: LeagueCard[]; tasks: Task[] }>(
      '/api/director/dashboard',
    ),
  });

  /*
    AN INSTRUCTOR'S DASHBOARD (2026-10-16): this week's sessions and the leagues they run. Nothing
    about membership, creating a league, getting started or a director's tasks — none of it is hers,
    and the server would refuse the reads anyway.
  */
  const nightsOnly = useRunsOnlyNights();
  const creation = useLeagueCreationAccess(!nightsOnly);
  const meta = useQuery<{ brand?: string }>({ queryKey: ['meta'], queryFn: () => apiFetch('/api/meta'), staleTime: Infinity });

  const leagueRows = dashboard.data?.leagues ?? [];
  const lifecycles = useDirectorLifecycle(
    leagueRows.map((league) => ({ status: league.status, sessions: league.lifecycleSessions })),
  );
  const currentLeagues = leagueRows.flatMap((league, index) => {
    const lifecycle = lifecycles[index]!;
    return lifecycle.state === 'completed' ? [] : [{ league, lifecycle }];
  });

  const onboarding = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => apiFetch<OnboardingResponse>('/api/me/onboarding'),
    enabled: !nightsOnly && dashboard.isSuccess && currentLeagues.length > 0,
  });
  // Reuse server-derived progress; only unfinished steps with an existing destination are actionable.
  const steps = onboarding.isSuccess && !onboarding.data.onboarding?.complete
    ? (onboarding.data.onboarding?.steps ?? []).filter(step => !step.done && step.href && step.key !== 'league')
    : [];

  if (dashboard.isPending) {
    return <p className="px-6 py-10 text-[14px] text-ink-2">Loading…</p>;
  }

  if (dashboard.isError) return <p role="alert" className="px-6 py-10">Could not load your leagues. Please try again.</p>;

  const completedLeagueIds = new Set(
    leagueRows.filter((_, index) => lifecycles[index]?.state === 'completed').map((league) => league.id),
  );
  const sessions = (dashboard.data?.sessions ?? []).filter(
    (session) => !completedLeagueIds.has(session.leagueId),
  );
  const tasks = dashboard.data?.tasks ?? [];

  /* The label is a full name; the greeting takes the first word of it and nothing more. */
  const firstName = actor.label.trim().split(/\s+/)[0] ?? '';

  if (currentLeagues.length === 0 && nightsOnly) {
    return <div className="mx-auto w-full min-w-0 max-w-5xl px-6 py-8">
      <h1 className="font-display text-3xl leading-tight">{firstName ? `Welcome, ${firstName}.` : 'Welcome.'}</h1>
      <p className="mt-5 text-[15px] text-ink-2">No leagues to run yet.</p>
    </div>;
  }

  if (currentLeagues.length === 0) {
    if (!creation.ready || meta.isPending) return <p role="status" className="px-6 py-10 text-[14px] text-ink-2">Membership details are loading or unavailable. Please try again.</p>;
    const firstLeague = leagueRows.length === 0 && creation.writable;
    const capacity = creation.allowance!.billingMode === 'purchased' ? creation.allowance!.purchasedAllowance : creation.allowance!.included;
    return <div className="mx-auto w-full min-w-0 max-w-5xl px-6 py-8">
      <h1 className="font-display text-3xl leading-tight">{firstLeague
        ? `Welcome to ${meta.data?.brand ?? 'the league'}${firstName ? `, ${firstName}` : ''}.`
        : leagueRows.length > 0 ? 'Ready to bring everyone together again?' : 'Your leagues'}</h1>
      {firstLeague ? <div className="mt-5 max-w-2xl space-y-4 text-[15px] leading-relaxed">
        <p>I’m so glad you’re here. Whether you’re bringing an established group or welcoming players for the first time, let’s get your league started.</p>
        <p>Begin with a few details about your league. You’ll build from there, with support along the way.</p>
      </div> : leagueRows.length > 0 ? <p className="mt-5 max-w-2xl text-[15px] leading-relaxed">Create another league when you’re ready. Your past leagues are still available to revisit.</p> : null}
      {creation.mayCreate ? <Link to="/director/leagues/new" className={`mt-6 ${buttonClass('primary')}`}>{firstLeague ? 'Create your first league' : 'Create a league'}</Link>
        : <p className="mt-5 text-[14px]">{creation.full ? creation.fullNote : <Link className={LINK_CLASS} to="/director/settings">Review your membership to create a league.</Link>}</p>}
      {leagueRows.length > 0 ? <NeedsYou tasks={[]} steps={[]} ready={creation.ready} writable={creation.writable} /> : null}
      {firstLeague ? <>
        <p className="mt-3 text-[14px] text-ink-2">{creation.trialNote ?? (capacity == null ? 'Your membership includes unlimited league spaces.' : `Your membership includes room for ${capacity} leagues.`)}</p>
        <Link to="/director/notes" className={`mt-5 inline-block text-[14px] ${LINK_CLASS}`}>Have questions along the way?</Link>
      </> : leagueRows.length > 0 ? <Link to="/director/leagues/archived" className={`mt-5 block w-fit text-[14px] ${LINK_CLASS}`}>View past leagues</Link> : null}
    </div>;
  }

  return (
    <div className="mx-auto flex w-full min-w-0 max-w-5xl flex-col gap-10 px-6 py-8">
      <section>
        {/*
          ⚠ AN `h1`, AND IT HAD STOPPED BEING ONE. The rewrite on 2026-09-06 replaced this page's
          hero — whose `h1` carried the league name — with this greeting, and rendered it as a `<p>`
          at display size: it looks like the page's title and announces as body copy. The axe scan
          catches it as `page-has-heading-one`, and it had been failing for six days, which is how
          we know the browser suite had not been run since that commit.

          ⚠ AND IT IS NO LONGER CONDITIONAL. The greeting was wrapped in `firstName ? … : null`, so
          a director whose label did not split into a word got a page with no level-one heading at
          all — a rarely-taken branch that removed the page's title. The name drops out of the
          sentence instead; the heading stays.

          It is genuinely the right `h1` rather than a convenient one: it opens the page, it is a
          step above the section headings, and the page is hers.
        */}
        <h1 className="mb-7 font-display text-3xl leading-tight">
          {firstName ? `Welcome, ${firstName}.` : 'Welcome.'}
        </h1>
        <h2 className="font-display text-2xl leading-tight">This Week</h2>

        {sessions.length === 0 ? (
          <p className="mt-3 text-[15px] text-ink-2">No sessions scheduled this week.</p>
        ) : (
          /*
            One card takes the full width; beyond that the grid decides, with no cap.

            Two implicit rows per card, so the cards can share tracks — see SessionCard. Always a
            grid, including for a single card, because the card declares `grid-rows-subgrid` and a
            subgrid outside a grid is not one.
          */
          <div
            className={
              sessions.length === 1
                ? 'mt-4 grid gap-4'
                : 'mt-4 grid gap-4 lg:grid-cols-2'
            }
          >
            {sessions.map((session) => (
              <SessionCard key={session.sessionId} session={session} nightsOnly={nightsOnly} />
            ))}
          </div>
        )}
      </section>

      {nightsOnly ? null : <NeedsYou tasks={tasks} steps={steps} ready={creation.ready} writable={creation.writable} />}

      <section>
        <h2 className="font-display text-2xl leading-tight">Your Leagues</h2>
        {nightsOnly ? null : (
          <Link
            to="/director/leagues/archived"
            className={`mt-2 inline-block text-[13px] ${LINK_CLASS}`}
          >
            Past leagues
          </Link>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {currentLeagues.map(({ league, lifecycle }) => <LeagueTile key={league.id} league={league} lifecycle={lifecycle} />)}
        </div>
      </section>
    </div>
  );
}

/**
 * One session in the current week.
 *
 * The metric row spans the full width UNDER the actions rather than sitting beside them, which is
 * what lets several of these sit side by side without growing taller.
 */
function SessionCard({ session, nightsOnly = false }: { session: Session; nightsOnly?: boolean }) {
  /*
    A session earlier in the week keeps its card until the week ends. `daysAway` is negative then,
    and "In -2 days" is the reason this is not simply a number. The weekday comes from the session's
    own zone, so a Monday league reads "Played Monday" wherever the director happens to be.
  */
  /*
    WHETHER THE NIGHT HAS BEGUN, as an instant rather than a day. `startsAt` is a moment in time, so
    comparing it to now is correct in every zone at once — the league's zone is what turned it into
    7pm on the card, and no second conversion is needed to ask whether 7pm has arrived.

    NOT whether a score exists. That switched the action under the director the moment one table
    handed a card in, so two sessions both finished read differently for a reason nobody could see.
  */
  const started = new Date(session.startsAt).getTime() <= Date.now();
  const historicalReview = session.scoringState === 'review' || session.scoringState === 'closed';
  const openScores = historicalReview || (started && session.scoringState === 'ready');
  /* The roster is the director's: an instructor's card always opens the night itself. */
  const needsRoster = !nightsOnly && session.playing === 0 && !historicalReview;
  const actionLabel = needsRoster ? 'Add or import players' : historicalReview ? 'View existing scores' : openScores ? 'Open Score Sheet'
    : session.scoringState === 'draft' ? (session.seatingValid ? 'Review and finalize seating' : 'Finish arranging tables') : 'Open Seating Board';

  const eyebrow =
    session.daysAway < 0
      ? `Scheduled ${when(session.startsAt, session.timeZone, { weekday: 'long' })}`
      : session.daysAway === 0
        ? `Today · ${when(session.startsAt, session.timeZone, { hour: 'numeric', minute: '2-digit' })}`
        : session.daysAway === 1
          ? 'Tomorrow'
          : `In ${session.daysAway} days`;

  const absentNote =
    session.absences === 0
      ? 'No absences'
      : `${session.absences} absent${session.subs > 0 ? ` · ${session.subs} sub` : ''}`;

  return (
    /*
      The card spans two of the enclosing grid's rows and borrows them as its own, so the detail
      area and the metric row are sized across every card beside it: the divider lands at one
      height whether or not a league has a venue, without reserving a guessed number of pixels.
      Its own row gap is zero — the parent's gap belongs between cards, not inside one.
    */
    <section className="row-span-2 grid grid-rows-subgrid gap-y-0 overflow-hidden rounded-md bg-deep p-5 text-on-deep">
      {/* Never wraps: the actions stay at the top right however long the name and venue run. */}
      <div className="flex items-start justify-between gap-4 pb-5">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-on-deep-3">
            {eyebrow}
          </p>

          <h3 className="mt-1.5 font-display text-xl leading-tight text-on-deep">
            {session.leagueName}
          </h3>
          <p className="mt-1 text-[13px] text-on-deep-2">
            Session {session.sessionNumber} of {session.sessionTotal} ·{' '}
            {when(session.startsAt, session.timeZone, { weekday: 'long', month: 'long', day: 'numeric' })}
            {session.venueName ? ` · ${session.venueName}` : ''}
          </p>
        </div>

        <div className="flex min-w-0 max-w-[50%] shrink-0 flex-col gap-2">
          <Link
            to={needsRoster ? `/director/leagues/${session.leagueId}/roster` : `/director/sessions/${session.sessionId}/${openScores ? 'scores' : 'seating'}`}
            className={`text-center ${buttonClass('secondaryOnDeep', 'inline')}`}
          >
            {actionLabel}
          </Link>
        </div>
      </div>

      {/* Full width, beneath the actions — three balanced columns. */}
      <dl className="grid grid-cols-3 gap-3 border-t border-on-deep/15 pt-4 text-[13px]">
        <Count label="Playing" value={`${session.playing}`} note="on the roster today" />
        <Count label="Tables" value={session.tables === 0 ? '—' : `${session.tables}`} />
        <Count label="Absent" value={`${session.absences}`} note={absentNote} />
      </dl>
    </section>
  );
}

/**
 * A metric on a NAVY ground, which is a gap in the kit rather than a fork of it.
 *
 * `MetricTile` is the right component for this shape and cannot be used here: it renders a `Card`,
 * which is white on a light border, so it cannot sit on a deep panel. Rather than pass it a prop to
 * turn its own card off — which is how a component ends up with two personalities — this is a local
 * four-line thing and the gap is written down. If a second deep surface ever needs metrics, that is
 * the moment `MetricTile` grows a `ground` prop, not before.
 */
function Count({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-[0.11em] text-on-deep-3">
        {label}
      </dt>
      <dd className="mt-1">
        <span className="tabular font-display text-xl leading-none text-on-deep">{value}</span>
        {note ? (
          <span className="mt-0.5 block text-[12px] leading-snug text-on-deep-2">{note}</span>
        ) : null}
      </dd>
    </div>
  );
}

/**
 * One evidence-driven primary action and at most two optional recommendations.
 * Membership restrictions take precedence; opening an optional tool never records completion.
 */
function NeedsYou({ tasks, steps, ready, writable }: { tasks: Task[]; steps: Step[]; ready: boolean; writable: boolean }) {
  const queryClient = useQueryClient();
  const guidance = useQuery({ queryKey: ['director-next-steps'], queryFn: () => apiFetch<DirectorGuidanceResponse>('/api/director/next-steps'), refetchInterval: 30_000 });
  const clear = useMutation({
    mutationFn: (step: RecommendationScope | 'playbookRead') => apiPost<OnboardingStepResponse>('/api/me/onboarding', { step, done: true }),
    onSuccess: async data => { queryClient.setQueryData(['onboarding'], data); await queryClient.invalidateQueries({ queryKey: ['director-next-steps'] }); },
  });
  if (!ready) return <p role="status">Loading your membership details…</p>;
  if (!writable) return <section><h2 className="font-display text-2xl">Next Steps</h2><Link className={`mt-3 inline-block ${LINK_CLASS}`} to="/director/settings">Review your membership to make changes.</Link></section>;
  if (guidance.isPending) return <p role="status">Loading your next steps…</p>;
  if (guidance.isError) return <p role="alert">Could not load your next steps. Please try again.</p>;
  const primary = guidance.data.primary;
  const recommendations = guidance.data.recommendations;
  const playbook = steps.find(step => step.key === 'playbook');
  // Do not add onboarding noise while a session is underway.
  const showPlaybook = !!playbook && recommendations.length < 2 && !primary?.title.includes('underway');
  /*
    OPEN TASKS SHOW BESIDE THE PRIMARY STEP, not only in its absence (2026-09-26). The primary step is
    one per director and "Complete your scores" outranks everything — so a player who reported she
    cannot come on Wednesday was invisible on this page for as long as last week's card was
    unfinished, which is exactly when the director is looking at it. Five at most, in the task
    list's own order (soonest due first); the rest are a link away.

    No urgency bar on any of them: the primary step's button is this section's one signal element,
    and three red bars at rest would spend the budget three times over.
  */
  const openTasks = tasks.slice(0, 5);
  if (!primary && !recommendations.length && !showPlaybook && !openTasks.length) return null;
  const action = (item: DirectorNextStep, prominent: boolean) => <div className="mt-4 flex flex-wrap items-center gap-3">
    {item.actions.map((a, index) => a.href.startsWith('/api/') ? <a key={a.href} href={a.href} className={buttonClass('secondary', 'inline')}>{a.label}</a> : <Link key={a.label} to={a.href} className={prominent && index === 0 ? buttonClass('primary') : buttonClass('secondary', prominent ? 'app' : 'inline')}>{a.label}</Link>)}
    {item.recommendation ? <Button variant="secondary" size="inline" disabled={clear.isPending} onClick={() => clear.mutate(item.recommendation!)}>Skip this step</Button> : null}
  </div>;
  return <section>
    <h2 className="font-display text-2xl leading-tight">Next Steps</h2>
    {primary ? <Card className="mt-4 p-5">
      <p className="text-[12px] text-ink-3">{primary.leagueName}</p>
      <h3 className="mt-1 font-display text-2xl">{primary.title}</h3><p className="mt-2 text-[14px] leading-relaxed">{primary.body}</p>{action(primary, true)}
    </Card> : null}
    {openTasks.length ? <Card className="mt-4 px-4">
      {openTasks.map(task => <ActionQueueItem key={task.id} className="flex-wrap sm:flex-nowrap" title={task.title}
        urgency="when-you-can" why={[task.overdue ? 'Overdue.' : null, task.detail].filter(Boolean).join(' ')}
        action={<Link to={task.action?.href ?? "/director/tasks"} className={`text-[13px] ${LINK_CLASS}`}>{task.action?.label ?? "Open"}</Link>} />)}
      {tasks.length > openTasks.length ? <p className="py-3 text-[13px] text-ink-2"><Link to="/director/tasks" className={LINK_CLASS}>{tasks.length - openTasks.length} more on your task list</Link></p> : null}
    </Card> : null}
    {recommendations.length || showPlaybook ? <Card className="mt-4 px-4">
      {recommendations.map(item => <ActionQueueItem key={item.key} className="flex-wrap sm:flex-nowrap" title={item.title} urgency="when-you-can" why={item.body} action={action(item, false)} />)}
      {showPlaybook ? <ActionQueueItem title={playbook!.title} urgency="when-you-can" why={playbook!.what} className="flex-wrap sm:flex-nowrap" action={<div className="flex flex-wrap items-center gap-4"><Link to={playbook!.href!} onClick={() => clear.mutate('playbookRead')} className={`text-[13px] ${LINK_CLASS}`}>Open</Link><Button variant="secondary" size="inline" disabled={clear.isPending} onClick={() => clear.mutate('playbookRead')}>Skip this step</Button></div>} /> : null}
    </Card> : null}
    {clear.isError ? <p role="alert" className="mt-3 text-[13px]">Could not save your recommendation. Please try again.</p> : null}
  </section>;
}

function LeagueTile({ league, lifecycle }: { league: LeagueCard; lifecycle: DirectorLifecycleResult }) {
  const full = league.playerCap > 0 ? Math.round((league.enrolled / league.playerCap) * 100) : 0;

  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex items-start justify-end gap-3">
        <DirectorLifecycleBadge lifecycle={lifecycle} leagueStatus={league.status} />
      </div>

      <Link
        to={`/director/leagues/${league.id}`}
        className="font-display text-xl leading-tight underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-deep"
      >
        {league.name}
      </Link>

      {/*
        The fill against the league's OWN cap, never against 28. The cap is per-league and assuming
        the maximum is a mistake CLAUDE.md names outright.
      */}
      <div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-light">
          <div className="h-full bg-sage-fill" style={{ width: `${Math.min(100, full)}%` }} />
        </div>
        <p className="tabular mt-2 text-[13px] text-ink-2">
          {league.enrolled} of {league.playerCap} seats taken
        </p>
      </div>

      <p className="mt-auto text-[13px] text-ink-2">
        {league.sessionCount === 0
          ? 'No sessions scheduled yet.'
          : league.scoredSessions === league.sessionCount
            ? 'Every session scored.'
            : `${league.scoredSessions} of ${league.sessionCount} sessions scored.`}
      </p>
    </Card>
  );
}
